import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './CheckoutResponsePage.css';
import { PDFDownloadLink, Page, Text, View, Document, Image, StyleSheet } from '@react-pdf/renderer';
import QRCode from 'qrcode';
import logo from '../assets/Chasylmlogo.png';
import watermark from '../assets/Chasylmlogo.png';
import { Pane, Heading, Paragraph, Button } from 'evergreen-ui'; // Import Evergreen UI components

function CheckoutResponsePage() {
  const [transactionData, setTransactionData] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [qrCodeImage, setQrCodeImage] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const orderTrackingId = searchParams.get('OrderTrackingId');

  // Generate QR code
  useEffect(() => {
    if (transactionData) {
      const orderItemsDetails = transactionData.transaction.Order.OrderItems.map(
        (item) => `Product ID: ${item.productId}, Quantity: ${item.quantity}, Unit Price: ${item.unitPrice}, Total: ${item.totalPrice}`
      ).join('\n');

      const qrValue = `
        Customer Name: ${transactionData.transaction.Order.customerName},
        Customer Phone: ${transactionData.transaction.Order.customerPhone},
        Order Items: ${orderItemsDetails},
        Cart Total: ${transactionData.transaction.Order.cartTotal},
        Delivery Fee: ${transactionData.transaction.Order.deliveryFee},
        Total Amount: ${transactionData.transaction.Order.totalAmount},
        Currency: ${transactionData.transaction.currency},
        Payment Status: ${transactionData.transaction.status},
        Payment Method: ${transactionData.transaction.paymentMethod || 'N/A'},
        Transaction Date: ${transactionData.transaction.createdDate ? new Date(transactionData.transaction.createdDate).toLocaleString() : 'N/A'},
        Merchant Reference: ${transactionData.transaction.merchantReference},
        Confirmation Code: ${transactionData.transaction.confirmationCode || 'N/A'},
        Order Tracking ID: ${transactionData.transaction.order_tracking_id}
      `;

      QRCode.toDataURL(qrValue, { width: 100, margin: 2 }, (err, url) => {
        if (!err) {
          setQrCodeImage(url);
        } else {
          console.error('Error generating QR code:', err);
        }
      });
    }
  }, [transactionData]);

  // Fetch transaction status
  useEffect(() => {
    const fetchTransactionStatus = async () => {
      if (orderTrackingId) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/checkout-pesapal/transaction-status?OrderTrackingId=${orderTrackingId}`
          );
          if (!response.ok) {
            throw new Error(`Failed to fetch transaction status: ${response.status}`);
          }
          const data = await response.json();
          setTransactionData(data);
        } catch (err) {
          setError(err.message);
        }
      }
    };

    fetchTransactionStatus();
  }, [orderTrackingId]);

  const styles = StyleSheet.create({
    page: {
      padding: 30,
      position: 'relative',
      backgroundColor: '#F2EED7',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 20,
    },
    contactInfo: {
      top: 20,
      fontSize: 10,
      textAlign: 'right',
    },
    detailsText: {
      fontSize: 12,
      marginBottom: 4,
    },
    logo: {
      width: 100,
      height: 80,
      marginBottom: 10,
    },
    watermark: {
      position: 'absolute',
      width: '90%',
      height: '60%',
      opacity: 0.1,
      top: 100,
      left: 0,
    },
    table: {
      display: 'table',
      width: '100%',
      borderStyle: 'solid',
      borderWidth: 1,
      margin: '10px 0',
    },
    tableRow: {
      margin: 'auto',
      flexDirection: 'row',
    },
    tableCol: {
      borderStyle: 'solid',
      borderWidth: 1,
      padding: 8,
    },
    tableCell: {
      margin: 'auto',
      fontSize: 10,
    },
    title: {
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 10,
    },
    section: {
      marginBottom: 10,
    },
    qrCodeContainer: {
      textAlign: 'start',
      marginTop: 20,
      marginBottom: 20,
    },
    qrCode: {
      width: 100,
      height: 100,
      marginTop: 10,
    },
  });

  const MyDocument = () => (
    <Document>
      <Page style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <View style={styles.contactInfo}>
            <Text>info@chasylmtours.com</Text>
            <Text>Tel: +254720727776</Text>
            <Text>Nairobi, Kenya</Text>
          </View>
        </View>
        <Image src={watermark} style={styles.watermark} />
        <Text style={styles.title}>Chasylm Tours & Safaris Order Receipt</Text>
        <View style={styles.section}>
          <Text style={styles.detailsText}>Customer Name: {transactionData?.transaction.Order.customerName || 'N/A'}</Text>
          <Text style={styles.detailsText}>Phone: {transactionData?.transaction.Order.customerPhone || 'N/A'}</Text>
          <Text style={styles.detailsText}>Pickup Point: {transactionData?.transaction.Order.Location?.pickupPoint || 'N/A'}</Text>
        </View>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={{ ...styles.tableCol, width: '25%' }}>
              <Text style={styles.tableCell}>Product ID</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '25%' }}>
              <Text style={styles.tableCell}>Quantity</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '25%' }}>
              <Text style={styles.tableCell}>Unit Price (USD)</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '25%' }}>
              <Text style={styles.tableCell}>Total Price (USD)</Text>
            </View>
          </View>
          {transactionData?.transaction.Order.OrderItems?.map((item, index) => (
            <View style={styles.tableRow} key={index}>
              <View style={{ ...styles.tableCol, width: '25%' }}>
                <Text style={styles.tableCell}>{item.productId || 'N/A'}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '25%' }}>
                <Text style={styles.tableCell}>{item.quantity || '0'}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '25%' }}>
                <Text style={styles.tableCell}>{item.unitPrice || '0'}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '25%' }}>
                <Text style={styles.tableCell}>{item.totalPrice || '0'}</Text>
              </View>
            </View>
          ))}
        </View>
        <View style={styles.section}>
          <Text style={styles.detailsText}>Cart Total: {transactionData?.transaction.Order.cartTotal || '0'} USD</Text>
          <Text style={styles.detailsText}>Delivery Fee: {transactionData?.transaction.Order.deliveryFee || '0'} USD</Text>
          <Text style={styles.detailsText}>Total Amount: {transactionData?.transaction.Order.totalAmount || '0'} USD</Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.detailsText}>Merchant Reference: {transactionData?.transaction.merchantReference || 'N/A'}</Text>
          <Text style={styles.detailsText}>Order Tracking ID: {transactionData?.transaction.order_tracking_id || 'N/A'}</Text>
          <Text style={styles.detailsText}>Confirmation Code: {transactionData?.transaction.confirmationCode || 'N/A'}</Text>
          <Text style={styles.detailsText}>Payment Method: {transactionData?.transaction.paymentMethod || 'N/A'}</Text>
          <Text style={styles.detailsText}>Payment Status: {transactionData?.transaction.paymentStatusDescription || 'N/A'}</Text>
          <Text style={styles.detailsText}>
            Transaction Date: {transactionData?.transaction.createdDate ? new Date(transactionData.transaction.createdDate).toLocaleString() : 'N/A'}
          </Text>
        </View>
        {qrCodeImage && (
          <View style={styles.qrCodeContainer}>
            <Text style={styles.detailsText}>Scan for more details</Text>
            <Image src={qrCodeImage} style={styles.qrCode} />
          </View>
        )}
        <Text style={styles.detailsText}>Thank you for shopping with us!</Text>
      </Page>
    </Document>
  );

  if (error) {
    return (
      <Pane className="error" padding={16}>
        <Paragraph color="red">Error: {error}</Paragraph>
      </Pane>
    );
  }

  if (!transactionData) {
    return (
      <Pane className="loading" padding={16}>
        <Paragraph>Loading transaction status...</Paragraph>
      </Pane>
    );
  }

  return (
    <Pane className="response-container" padding={16} maxWidth={800} margin="auto">
      <Heading size={800} marginBottom={16}>
        Order Payment Status
      </Heading>
      <Pane marginBottom={16}>
        <Paragraph>
          <strong>Customer Name:</strong> {transactionData.transaction.Order.customerName}
        </Paragraph>
        <Paragraph>
          <strong>Phone:</strong> {transactionData.transaction.Order.customerPhone}
        </Paragraph>
        <Paragraph>
          <strong>Pickup Point:</strong> {transactionData.transaction.Order.Location?.pickupPoint || 'N/A'}
        </Paragraph>
      </Pane>

      <Pane marginBottom={16}>
        <Paragraph>
          <strong>Cart Total:</strong> {transactionData.transaction.Order.cartTotal || '0'} USD
        </Paragraph>
        <Paragraph>
          <strong>Delivery Fee:</strong> {transactionData.transaction.Order.deliveryFee || '0'} USD
        </Paragraph>
        <Paragraph>
          <strong>Total Amount:</strong> {transactionData.transaction.Order.totalAmount || '0'} USD
        </Paragraph>
        <Paragraph>
          <strong>Merchant Reference:</strong> {transactionData.transaction.merchantReference}
        </Paragraph>
        <Paragraph>
          <strong>Order Tracking ID:</strong> {transactionData.transaction.order_tracking_id}
        </Paragraph>
        <Paragraph>
          <strong>Confirmation Code:</strong> {transactionData.transaction.confirmationCode || 'N/A'}
        </Paragraph>
        <Paragraph>
          <strong>Payment Method:</strong> {transactionData.transaction.paymentMethod || 'N/A'}
        </Paragraph>
        <Paragraph>
          <strong>Payment Status:</strong> {transactionData.transaction.paymentStatusDescription}
        </Paragraph>
        <Paragraph>
          <strong>Transaction Date:</strong>{' '}
          {transactionData.transaction.createdDate
            ? new Date(transactionData.transaction.createdDate).toLocaleString()
            : 'N/A'}
        </Paragraph>
      </Pane>

      <PDFDownloadLink document={<MyDocument />} fileName="order_receipt.pdf">
        {({ loading }) => (
          <Button appearance="primary" isLoading={loading}>
            {loading ? 'Loading document...' : 'Download receipt'}
          </Button>
        )}
      </PDFDownloadLink>
    </Pane>
  );
}

export default CheckoutResponsePage;